import cn from 'classnames'
import React, { ReactElement, useState } from 'react'
import MediaQuery from 'react-responsive'

import { enableLoginWithUsername } from '@flowwai-frontend/bps-officer-web/common/data-access'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import {
   ArrowLeftIcon,
   ButtonRightArrowIcon
} from '@flowwai-frontend/design-system/icons'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { removeSpacesFromGivenString } from '@flowwai-frontend/shared/utils/common'

import {
   loginMachineEvents,
   loginMachineTags,
   NO_OF_OTP_BOXES,
   stepNames
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'

import { OTPFieldWithResend } from '../OTPFieldWithResend/OTPFieldWithResend'

import './index.css'
import * as Styles from './styles'

interface VerificationPageProps {
   loginMachineState: LoginMachineState
   loginMachineSend: LoginMachineSend
   otpFieldsCount: number
   buttonText?: string
   timerRef: React.MutableRefObject<{
      start: () => void
   } | null>
   setShowLoginTransition: React.Dispatch<React.SetStateAction<boolean>>
}

export const OtpVerificationStep = (
   props: VerificationPageProps
): ReactElement => {
   const { verificationStep } = stepNames

   const {
      otpFieldsCount = 6,
      buttonText,
      loginMachineState,
      loginMachineSend,
      timerRef,
      setShowLoginTransition
   } = props

   const { maskedPhoneNumber, email, errorMessage } = loginMachineState.context

   const isError = !!errorMessage

   const t = useCustomT('auth:login.verifyOTP')

   const { theme } = useAppTheme()
   const { otpVerificationStep: otpCardTheme } = theme.auth.login.otp

   const [otpValue, setOTPValue] = useState('')

   const hasMobileNumber = maskedPhoneNumber ? t('mobileNo') : ''

   const isOTPScreen = loginMachineState.hasTag(loginMachineTags.otpScreen)

   const onVerifyOTP = (event: React.SyntheticEvent): void => {
      event.preventDefault()

      if (enableLoginWithUsername) {
         loginMachineSend({
            type: loginMachineEvents.verifyUsernameOtp,
            otp: otpValue
         })
         return
      }

      loginMachineSend({
         type: loginMachineEvents.verifyEmailOtp,
         otp: otpValue
      })
   }

   const getButtonText = (): string =>
      buttonText ? buttonText : t('buttonText')

   const renderOTPFooter = (): ReactElement => (
      <div className={cn(Styles.buttonContainerStyles)}>
         <Button
            type='submit'
            size='Large'
            hierarchy={Hierarchy.Primary}
            subVariant={SubVariant.Primary}
            className={cn(Styles.verifyOtpButtonStyles, 'register-for-webinar')}
            isLoading={loginMachineState.hasTag(
               loginMachineTags.verifyOtpLoading
            )}
            isDisabled={!(otpValue.length === NO_OF_OTP_BOXES)}
         >
            {getButtonText()}
            <MediaQuery maxWidth={768}>
               <ButtonRightArrowIcon
                  height={12}
                  width={14}
                  className='button-right-icon'
               />
            </MediaQuery>
         </Button>
         {isError ? (
            <p
               className={cn(
                  Styles.errorMessageStyles,
                  otpCardTheme.errorTextColor,
                  'otp-error-message-transition'
               )}
            >
               {errorMessage}
            </p>
         ) : (
            <p className={cn(Styles.errorMessageStyles)}></p>
         )}
      </div>
   )

   const onChangeOTP = (otp: string) => {
      setOTPValue(otp)

      loginMachineSend({
         type: loginMachineEvents.onChangeOTP
      })
   }

   const renderOTPBody = (): ReactElement => (
      <OTPFieldWithResend
         numInputs={otpFieldsCount}
         currentStep={verificationStep}
         loginMachineState={loginMachineState}
         loginMachineSend={loginMachineSend}
         otpValue={otpValue}
         setOTPValue={onChangeOTP}
         timerRef={timerRef}
      />
   )

   const onClickBack = (): void => {
      loginMachineSend(loginMachineEvents.goBack)
      setShowLoginTransition(true)
   }

   const renderOTPHeader = (): ReactElement => (
      <>
         <MediaQuery minWidth={769}>
            <Button
               hierarchy={Hierarchy.Tertiary}
               type='button'
               size='Small'
               subVariant={SubVariant.Gray}
               className={cn(Styles.leftArrowButtonStyles)}
               onClick={onClickBack}
            >
               <ArrowLeftIcon width={24} height={24} />
            </Button>
         </MediaQuery>
         <span className={cn(Styles.otpTextStyles, otpCardTheme.otpTextColor)}>
            {t('title')}
         </span>
         <div className={cn(Styles.otpDescriptionWrapperStyles)}>
            <span
               className={cn(
                  Styles.otpDescriptionTextStyles,
                  otpCardTheme.otpDescriptionTextColor
               )}
            >
               <span>{t('pleaseEnterOtpText')} </span>
               <br />
               {maskedPhoneNumber ? (
                  <span>
                     <span
                        className={cn(
                           Styles.emailAndNumberStyles,
                           otpCardTheme.emailAndNumberTextColor
                        )}
                     >
                        {`${removeSpacesFromGivenString(maskedPhoneNumber)} `}
                     </span>
                     {`${t('and')} `}
                  </span>
               ) : null}
               <span
                  className={cn(
                     Styles.emailAndNumberStyles,
                     otpCardTheme.emailAndNumberTextColor
                  )}
               >
                  {email}
               </span>
            </span>
         </div>
      </>
   )

   return (
      <form
         className={cn(Styles.containerStyles, {
            'otp-enter-transition': isOTPScreen
         })}
         onSubmit={onVerifyOTP}
      >
         {renderOTPHeader()}
         {renderOTPBody()}
         {renderOTPFooter()}
      </form>
   )
}
